import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Environment, EnvironmentConfig, getDefaultEnvironment, setEnvironment, ENVIRONMENT_CONFIGS } from '../config/environment';

interface EnvironmentContextType {
  environment: Environment;
  config: EnvironmentConfig;
  switchEnvironment: (env: Environment) => void;
  isAdmin: boolean;
  isStudent: boolean;
  canSwitchEnvironment: boolean;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

interface EnvironmentProviderProps {
  children: ReactNode;
}

export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({ children }) => {
  const [environment, setEnvironmentState] = useState<Environment>(getDefaultEnvironment());
  const [config, setConfig] = useState<EnvironmentConfig>(ENVIRONMENT_CONFIGS[environment]);
  
  // Check if environment switching is allowed (not locked by build-time config)
  const canSwitchEnvironment = !process.env.REACT_APP_BUILD_ENVIRONMENT;

  useEffect(() => {
    setConfig(ENVIRONMENT_CONFIGS[environment]);
  }, [environment]);

  const switchEnvironment = useCallback((env: Environment): void => {
    if (canSwitchEnvironment) {
      setEnvironmentState(env);
      setEnvironment(env);
    }
  }, [canSwitchEnvironment]);

  const value: EnvironmentContextType = {
    environment,
    config,
    switchEnvironment,
    isAdmin: environment === 'admin',
    isStudent: environment === 'student',
    canSwitchEnvironment,
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};
